/**
 * LootSystem -- Generates items with random affixes and rarity.
 *
 * Reads item templates and affix definitions from items.json via DataLoader.
 * Supports rarity tiers: Normal, Magic, Rare, Unique, Legendary.
 * Generates affixes based on rarity and item slot.
 *
 * @module LootSystem
 */

import dataStore from './DataLoader.js';
import eventBus from '../core/EventBus.js';

/** Rarity tiers in ascending order. */
const RARITY_ORDER = ['normal', 'magic', 'rare', 'unique', 'legendary'];

/** Default rarity weights (percentages). */
const RARITY_WEIGHTS = {
  normal: 50,
  magic: 30,
  rare: 15,
  unique: 4,
  legendary: 1,
};

/** Number of affixes per rarity tier. */
const AFFIX_COUNTS = {
  normal: [0, 0],
  magic: [1, 2],
  rare: [3, 4],
  unique: [0, 0],   // Uniques use fixedAffixes
  legendary: [0, 0], // Legendaries use fixedAffixes
};

/** Rarity display colors (CSS). */
const RARITY_COLORS = {
  normal: '#cccccc',
  magic: '#6688ff',
  rare: '#ffcc00',
  unique: '#ff8800',
  legendary: '#ff3333',
};

/** Rarity display names. */
const RARITY_NAMES = {
  normal: 'Normal',
  magic: 'Magic',
  rare: 'Rare',
  unique: 'Unique',
  legendary: 'Legendary',
};

let _nextItemId = 1;

class LootSystem {
  constructor() {
    this._itemData = null;
  }

  /**
   * Get the item data from the data store, with caching.
   * @returns {object}
   */
  _getItemData() {
    if (this._itemData) return this._itemData;
    this._itemData = dataStore.getAll('items');
    return this._itemData;
  }

  /**
   * Roll a rarity tier.
   * @param {number} [luckBonus=0] - Bonus percentage added to higher rarities.
   * @returns {string} rarity key
   */
  rollRarity(luckBonus = 0) {
    const rand = Math.random() * 100;
    let cumulative = 0;

    // Adjust weights with luck bonus
    const weights = { ...RARITY_WEIGHTS };
    if (luckBonus > 0) {
      weights.normal = Math.max(10, weights.normal - luckBonus * 2);
      weights.magic += luckBonus * 0.5;
      weights.rare += luckBonus * 0.8;
      weights.unique += luckBonus * 0.5;
      weights.legendary += luckBonus * 0.2;
    }

    // Normalize and roll
    const total = Object.values(weights).reduce((a, b) => a + b, 0);
    const normalized = {};
    for (const [key, val] of Object.entries(weights)) {
      normalized[key] = (val / total) * 100;
    }

    for (const rarity of RARITY_ORDER) {
      cumulative += normalized[rarity];
      if (rand < cumulative) return rarity;
    }
    return 'normal';
  }

  /**
   * Generate a random item appropriate to a given level.
   * @param {number} level - Character/party level
   * @param {string} [forceRarity] - Force a specific rarity
   * @param {string} [forceSlot] - Force a specific slot
   * @returns {object} Generated item instance
   */
  generateItem(level, forceRarity, forceSlot) {
    const data = this._getItemData();
    if (!data) return this._createFallbackItem(level);

    const rarity = forceRarity || this.rollRarity();

    // For unique/legendary, try to find a matching unique item
    if ((rarity === 'unique' || rarity === 'legendary') && data.uniques) {
      const eligible = data.uniques.filter(u => {
        if (forceSlot && u.slot !== forceSlot) return false;
        return u.level <= level + 2;
      });
      if (eligible.length > 0) {
        const template = eligible[Math.floor(Math.random() * eligible.length)];
        return this._createUniqueItem(template, rarity);
      }
      // Fall back to rare if no matching unique found
      return this.generateItem(level, 'rare', forceSlot);
    }

    // Pick a random base template for the level
    const templates = data.templates || [];
    const eligible = templates.filter(t => {
      if (forceSlot && t.slot !== forceSlot) return false;
      const [minLvl, maxLvl] = t.levelRange || [1, 15];
      return level >= minLvl - 1 && level <= maxLvl + 2;
    });

    if (eligible.length === 0) {
      return this._createFallbackItem(level);
    }

    const template = eligible[Math.floor(Math.random() * eligible.length)];
    return this._createItemFromTemplate(template, level, rarity, data.affixes);
  }

  /**
   * Generate loot from an enemy's loot table.
   * @param {object} lootTable - Enemy loot table { goldRange, itemChance, itemPool }
   * @param {number} level - Party level
   * @returns {{ gold: number, items: object[], rolls: object[] }}
   */
  generateLoot(lootTable, level) {
    const result = { gold: 0, items: [], rolls: [] };

    if (!lootTable) return result;

    // Gold
    if (lootTable.goldRange) {
      const [min, max] = lootTable.goldRange;
      result.gold = min + Math.floor(Math.random() * (max - min + 1));
    }

    // Item drops
    const itemChance = lootTable.itemChance || 0;
    if (Math.random() < itemChance) {
      const rarity = this.rollRarity();
      const roll = Math.floor(Math.random() * 100) + 1;
      const item = this.generateItem(level, rarity);

      result.items.push(item);
      result.rolls.push({
        roll,
        rarity: item.rarity,
        rarityName: RARITY_NAMES[item.rarity] || 'Normal',
        itemName: item.name,
      });
    }

    return result;
  }

  /**
   * Create an item instance from a base template with affixes.
   * @private
   */
  _createItemFromTemplate(template, level, rarity, affixData) {
    const item = {
      instanceId: `item_${_nextItemId++}`,
      templateId: template.id,
      name: template.name,
      slot: template.slot,
      category: template.category,
      rarity,
      level: level,
      baseStats: { ...template.baseStats },
      affixes: [],
      totalStats: { ...template.baseStats },
    };

    // Scale base stats slightly by level
    const levelScale = 1 + (level - (template.levelRange?.[0] || 1)) * 0.05;
    for (const [stat, val] of Object.entries(item.baseStats)) {
      if (typeof val === 'number') {
        item.baseStats[stat] = Math.round(val * levelScale);
      }
    }

    // Generate affixes for magic/rare
    if (rarity === 'magic' || rarity === 'rare') {
      const [minAffixes, maxAffixes] = AFFIX_COUNTS[rarity];
      const numAffixes = minAffixes + Math.floor(Math.random() * (maxAffixes - minAffixes + 1));
      const affixes = this._rollAffixes(numAffixes, item.slot, rarity, level, affixData);
      item.affixes = affixes;

      // Build item name with prefix/suffix
      const prefix = affixes.find(a => a.type === 'prefix');
      const suffix = affixes.find(a => a.type === 'suffix');
      if (prefix) item.name = `${prefix.name} ${item.name}`;
      if (suffix) item.name = `${item.name} ${suffix.name}`;
    }

    // Recalculate total stats
    item.totalStats = this._calculateTotalStats(item);

    return item;
  }

  /**
   * Create an item from a unique/legendary template.
   * @private
   */
  _createUniqueItem(template, rarity) {
    const item = {
      instanceId: `item_${_nextItemId++}`,
      templateId: template.id,
      name: template.name,
      slot: template.slot,
      category: template.category,
      rarity: rarity || template.rarity || 'unique',
      level: template.level || 1,
      baseStats: { ...template.baseStats },
      affixes: (template.fixedAffixes || []).map(a => ({
        ...a,
        type: 'fixed',
        fixed: true,
      })),
      totalStats: { ...template.baseStats },
      isUnique: true,
    };

    item.totalStats = this._calculateTotalStats(item);
    return item;
  }

  /**
   * Create a fallback item if no template is found.
   * @private
   */
  _createFallbackItem(level) {
    return {
      instanceId: `item_${_nextItemId++}`,
      templateId: 'fallback',
      name: 'Worn Dagger',
      slot: 'weapon',
      category: 'dagger',
      rarity: 'normal',
      level,
      baseStats: { damage: 3 + level, accuracy: 5 },
      affixes: [],
      totalStats: { damage: 3 + level, accuracy: 5 },
    };
  }

  /**
   * Roll random affixes for an item.
   * @private
   */
  _rollAffixes(count, slot, rarity, level, affixData) {
    if (!affixData) return [];

    const rarityIdx = RARITY_ORDER.indexOf(rarity);
    const prefixes = (affixData.prefixes || []).filter(a => {
      const minIdx = RARITY_ORDER.indexOf(a.rarityMin || 'magic');
      return rarityIdx >= minIdx && this._slotMatches(a.slots, slot);
    });
    const suffixes = (affixData.suffixes || []).filter(a => {
      const minIdx = RARITY_ORDER.indexOf(a.rarityMin || 'magic');
      return rarityIdx >= minIdx && this._slotMatches(a.slots, slot);
    });

    const picked = [];
    const usedIds = new Set();

    // Try to get a mix of prefix and suffix
    let prefixCount = 0;
    let suffixCount = 0;
    const maxPrefixes = Math.ceil(count / 2);
    const maxSuffixes = count;

    for (let i = 0; i < count; i++) {
      const canPrefix = prefixCount < maxPrefixes && prefixes.length > 0;
      const canSuffix = suffixCount < maxSuffixes && suffixes.length > 0;

      if (!canPrefix && !canSuffix) break;

      const usePrefix = canPrefix && (!canSuffix || Math.random() < 0.5);
      const pool = usePrefix ? prefixes : suffixes;
      const available = pool.filter(a => !usedIds.has(a.id));
      if (available.length === 0) continue;

      const affix = available[Math.floor(Math.random() * available.length)];
      usedIds.add(affix.id);

      // Determine affix tier based on level (4 tiers: indices 0-3)
      const tier = Math.min(3, Math.floor(level / 4));

      const resolvedAffix = {
        id: affix.id,
        name: affix.name,
        type: usePrefix ? 'prefix' : 'suffix',
        effect: this._resolveAffixEffect(affix.effect, tier),
        description: this._describeEffect(affix.effect, tier),
      };

      picked.push(resolvedAffix);
      if (usePrefix) prefixCount++;
      else suffixCount++;
    }

    return picked;
  }

  /**
   * Check if slot matches affix allowed slots (handles ring1/ring2 -> ring).
   * @private
   */
  _slotMatches(allowedSlots, itemSlot) {
    if (!allowedSlots || allowedSlots.length === 0) return true;
    const normalizedSlot = itemSlot.replace(/\d+$/, ''); // ring1/ring2 -> ring
    return allowedSlots.includes(itemSlot) || allowedSlots.includes(normalizedSlot);
  }

  /**
   * Resolve an affix effect, picking the value for a specific tier.
   * @private
   */
  _resolveAffixEffect(effect, tier) {
    if (!effect) return effect;
    const resolved = { ...effect };
    if (Array.isArray(resolved.value)) {
      resolved.value = resolved.value[Math.min(tier, resolved.value.length - 1)];
    }
    // Handle nested attribute arrays
    if (resolved.attributes) {
      resolved.attributes = {};
      for (const [attr, vals] of Object.entries(effect.attributes)) {
        if (Array.isArray(vals)) {
          resolved.attributes[attr] = vals[Math.min(tier, vals.length - 1)];
        } else {
          resolved.attributes[attr] = vals;
        }
      }
    }
    return resolved;
  }

  /**
   * Generate a human-readable description for an affix effect.
   * @private
   */
  _describeEffect(effect, tier) {
    if (!effect) return '';

    const value = Array.isArray(effect.value)
      ? effect.value[Math.min(tier, effect.value.length - 1)]
      : effect.value;

    switch (effect.type) {
      case 'addDamage':
        return `+${value} ${effect.damageType || ''} damage`;
      case 'addStat':
        return `+${value} ${effect.stat}`;
      case 'addAttribute':
        return `+${value} ${(effect.attribute || '').toUpperCase()}`;
      case 'addResistance':
        return `+${value}% ${effect.damageType || ''} resistance`;
      case 'lifeSteal':
        return `${value}% life steal`;
      case 'percentDamageBonus':
        return `+${value}% ${effect.damageType || ''} damage`;
      case 'percentStatBonus':
        return `+${value}% ${effect.stat || ''}`;
      case 'critChanceBonus':
        return `+${value}% critical chance`;
      case 'critDamageBonus':
        return `+${value}% critical damage`;
      case 'reflectDamage':
        return `Reflects ${value} damage`;
      case 'hpRegenPerTurn':
        return `Regenerate ${value} HP per turn`;
      case 'dodgeChanceBonus':
        return `+${value}% dodge chance`;
      case 'multiAttribute': {
        const parts = [];
        for (const [attr, vals] of Object.entries(effect.attributes || {})) {
          const v = Array.isArray(vals)
            ? vals[Math.min(tier, vals.length - 1)]
            : vals;
          parts.push(`+${v} ${attr.toUpperCase()}`);
        }
        return parts.join(', ');
      }
      default:
        return effect.description || '';
    }
  }

  /**
   * Calculate total stats for an item (base + affix bonuses).
   * @private
   */
  _calculateTotalStats(item) {
    const stats = { ...item.baseStats };

    for (const affix of item.affixes) {
      const effect = affix.effect;
      if (!effect) continue;

      if (effect.type === 'addStat' && effect.stat && typeof effect.value === 'number') {
        stats[effect.stat] = (stats[effect.stat] || 0) + effect.value;
      }
      if (effect.type === 'addDamage' && typeof effect.value === 'number') {
        stats.damage = (stats.damage || 0) + effect.value;
      }
    }

    return stats;
  }

  // ── Static helpers ─────────────────────────────────

  /**
   * Get the CSS color for a rarity tier.
   * @param {string} rarity
   * @returns {string}
   */
  static getRarityColor(rarity) {
    return RARITY_COLORS[rarity] || RARITY_COLORS.normal;
  }

  /**
   * Get the display name for a rarity tier.
   * @param {string} rarity
   * @returns {string}
   */
  static getRarityName(rarity) {
    return RARITY_NAMES[rarity] || 'Normal';
  }

  /**
   * Get all rarity tier names in order.
   * @returns {string[]}
   */
  static getRarityOrder() {
    return [...RARITY_ORDER];
  }
}

const lootSystem = new LootSystem();
export default lootSystem;
export { RARITY_COLORS, RARITY_NAMES, RARITY_ORDER };
